import { useEffect, useState } from "react";
import {
  MDSCheckbox,
  MDSCheckboxGroup,
  MDSGridList,
  MDSRadio,
  MDSRadioGroup,
  MDSTextVariant,
  MDSButton,
  MDSButtonVariant,
} from "@/mds";
import { observer } from "mobx-react-lite";
import {
  StyledExplanation,
  StyledFormattingContentLeftAddon,
  StyledFormattingContentRightAddon,
  StyledHeading,
  StyledSubExplanation,
  StyledSubTitle,
} from "@/pages/integrations/shared/integrations.styles";
import { MDSText, MDSTextColor, MDSTextWeight } from "@/mds/components";
import { useAppStore } from "@/store";
import {
  EmailIntegrationFormattingMode,
  EmailIntegrationOrganizingMode,
  EmailIntegrationFormattingInstructions,
  EmailIntegrationOrganizingInstructions,
} from "@/store/email-senders/types";
import { IntegrationsFormattingCollections } from "@/pages/integrations/shared/integrations-formatting-collections";
import { MDSGridListItemProps } from "@/mds/components/lists/grid-list-item";
import {
  Container,
  Section,
  OptionLabel,
  ExtraContentContainer,
  ExtraContent,
  ExtraContentInformation,
  LeftStyles,
  StyledHorizontalDivider,
} from "@/pages/integrations/shared/integrations-formatting.styles";
import { StyledTextField } from "@/pages/integrations/shared/integrations-formatting.styles";

export const IntegrationsFormattingForEmail = observer(() => {
  const { store } = useAppStore();
  const {
    formattingMode: formattingOption,
    formattingInstructions,
    organizingOption,
    organizingInstructions,
    setFormattingOption,
    setFormattingInstructions,
    setOrganizingOption,
    setOrganizingInstructions,
    organizingManualCollections,
    setOrganizingManualCollections,
  } = store.emailSenders;

  const [localFormattingInstructions, setLocalFormattingInstructions] =
    useState<EmailIntegrationFormattingInstructions>(formattingInstructions);
  const [localOrganizingInstructions, setLocalOrganizingInstructions] =
    useState<EmailIntegrationOrganizingInstructions>(organizingInstructions);

  useEffect(() => {
    setLocalFormattingInstructions(formattingInstructions);
    setLocalOrganizingInstructions(organizingInstructions);
  }, [formattingInstructions, organizingInstructions]);

  // Compute show states from local instructions
  const showFormattingCustomize = localFormattingInstructions !== null;
  const showOrganizingCustomize = localOrganizingInstructions !== null;

  const isFormattingSaveDisabled = localFormattingInstructions === formattingInstructions;
  const isOrganizingSaveDisabled = localOrganizingInstructions === organizingInstructions;

  const showSpecificCollections = organizingOption.includes("manual");

  // Generate formatting option items
  const formattingItems: MDSGridListItemProps[] = [
    {
      id: "simple",
      textValue: "Simple",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSRadio value="simple" aria-label="Simple formatting" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Simple
          </MDSText>
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            Preserve as much as the original formatting as possible
          </MDSText>
        </OptionLabel>
      ),
    },
    {
      id: "smart",
      textValue: "Smart",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSRadio value="smart" aria-label="Smart formatting" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Smart
          </MDSText>
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            Allow Mem to pick the best format to capture key details for each email you save
          </MDSText>
          {showFormattingCustomize && (
            <>
              <StyledHorizontalDivider />
              <ExtraContentContainer>
                <ExtraContent>
                  <ExtraContentInformation>
                    <StyledSubTitle variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                      Customize how emails are formatted
                    </StyledSubTitle>
                    <StyledSubExplanation variant={MDSTextVariant.ParagraphS}>
                      Provide more guidance to how to format notes from different kinds of emails
                    </StyledSubExplanation>
                  </ExtraContentInformation>
                  <StyledTextField
                    isTextArea
                    name="formattingInstructions"
                    aria-label="Customize how emails are formatted"
                    textAreaProps={{ rows: 3 }}
                    value={localFormattingInstructions}
                    onChange={setLocalFormattingInstructions}
                    placeholder="For newsletters, list key points and preserve all resource links. For flight confirmations, only include confirmation codes and travel details. For customer intros, create meeting briefing notes. For customer intros, create meeting briefing notes"
                  />
                  <LeftStyles>
                    <MDSButton
                      onClick={() => {
                        // revert to local value
                        setLocalFormattingInstructions(formattingInstructions);
                      }}
                    >
                      Cancel
                    </MDSButton>
                    <MDSButton
                      variant={MDSButtonVariant.Contrast}
                      onClick={() => setFormattingInstructions(localFormattingInstructions)}
                      isDisabled={isFormattingSaveDisabled}
                    >
                      Save Settings
                    </MDSButton>
                  </LeftStyles>
                </ExtraContent>
              </ExtraContentContainer>
            </>
          )}
        </OptionLabel>
      ),
      rightAddon: !showFormattingCustomize ? (
        <StyledFormattingContentRightAddon>
          <MDSButton onClick={() => setLocalFormattingInstructions("")}>Customize</MDSButton>
        </StyledFormattingContentRightAddon>
      ) : null,
    },
  ];

  // Generate organizing option items
  const organizingItems: MDSGridListItemProps[] = [
    {
      id: "specific-collections",
      textValue: "Specific Collections",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="manual" aria-label="Specific collections" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Specific Collections
          </MDSText>
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            Always add notes created from email to one or more collections
          </MDSText>
          {showSpecificCollections && (
            <>
              <StyledHorizontalDivider />
              <ExtraContentContainer>
                <ExtraContent>
                  <IntegrationsFormattingCollections
                    organizingManualCollections={organizingManualCollections}
                    setOrganizingManualCollections={setOrganizingManualCollections}
                  />
                </ExtraContent>
              </ExtraContentContainer>
            </>
          )}
        </OptionLabel>
      ),
    },
    {
      id: "smart-organizing",
      textValue: "Smart organizing",
      leftAddon: (
        <StyledFormattingContentLeftAddon>
          <MDSCheckbox value="smart" aria-label="Smart organizing" />
        </StyledFormattingContentLeftAddon>
      ),
      children: (
        <OptionLabel>
          <MDSText
            variant={MDSTextVariant.TextS}
            weight={MDSTextWeight.Smedium}
            color={MDSTextColor.Primary}
          >
            Smart
          </MDSText>
          <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            Allow Mem to pick the collections to organize each email you save
          </MDSText>
          {showOrganizingCustomize && (
            <>
              <StyledHorizontalDivider />
              <ExtraContentContainer>
                <ExtraContent>
                  <ExtraContentInformation>
                    <StyledSubTitle variant={MDSTextVariant.TextS} weight={MDSTextWeight.Smedium}>
                      Customize how emails are organized
                    </StyledSubTitle>
                    <StyledSubExplanation variant={MDSTextVariant.ParagraphS}>
                      Provide more guidance to how to organize notes from different kinds of emails
                    </StyledSubExplanation>
                  </ExtraContentInformation>
                  <StyledTextField
                    isTextArea
                    textAreaProps={{ rows: 3 }}
                    aria-label="Customize how emails are organized"
                    value={localOrganizingInstructions}
                    onChange={setLocalOrganizingInstructions}
                    placeholder="For customer intros, include them in the generic customer intros collection as well as the relevant product collection"
                    name="organizingInstructions"
                  />
                  <LeftStyles>
                    <MDSButton
                      onClick={() => {
                        // revert to local value
                        setLocalOrganizingInstructions(organizingInstructions);
                      }}
                    >
                      Cancel
                    </MDSButton>
                    <MDSButton
                      variant={MDSButtonVariant.Contrast}
                      onClick={() => setOrganizingInstructions(localOrganizingInstructions)}
                      isDisabled={isOrganizingSaveDisabled}
                    >
                      Save Settings
                    </MDSButton>
                  </LeftStyles>
                </ExtraContent>
              </ExtraContentContainer>
            </>
          )}
        </OptionLabel>
      ),
      rightAddon: !showOrganizingCustomize ? (
        <StyledFormattingContentRightAddon>
          <MDSButton onClick={() => setLocalOrganizingInstructions("")}>Customize</MDSButton>
        </StyledFormattingContentRightAddon>
      ) : null,
    },
  ];

  return (
    <Container>
      <Section>
        <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
          Formatting
        </StyledHeading>
        <StyledExplanation variant={MDSTextVariant.ParagraphS}>
          Mem will automatically format your emails, adding an informative title and a concise
          summary of key points. You can further customize your note at any time.
        </StyledExplanation>
        <MDSRadioGroup
          aria-label="Formatting"
          value={formattingOption}
          onChange={value => {
            const typedValue = value as EmailIntegrationFormattingMode;
            setFormattingOption(typedValue);

            if (typedValue !== "smart") {
              // immediately hide the form
              setLocalFormattingInstructions(null);
            }
          }}
        >
          <MDSGridList items={formattingItems} disallowTypeAhead />
        </MDSRadioGroup>
      </Section>

      <Section>
        <StyledHeading variant={MDSTextVariant.TextXs} weight={MDSTextWeight.SemiBold}>
          Organizing
        </StyledHeading>
        <StyledExplanation variant={MDSTextVariant.ParagraphS}>
          Mem can automatically organize notes created from your emails
        </StyledExplanation>

        <MDSCheckboxGroup
          aria-label="Organizing"
          value={organizingOption}
          onChange={value => {
            const typedValue = value as EmailIntegrationOrganizingMode[];
            setOrganizingOption(typedValue);

            if (!typedValue.includes("smart")) {
              setLocalOrganizingInstructions(null);
            }
          }}
        >
          <MDSGridList items={organizingItems} disallowTypeAhead />
        </MDSCheckboxGroup>
      </Section>
    </Container>
  );
});
